<template>
  <div ref="rootEl">
    <wui-avatar
      :key="`${name}-${deferredImageUrl}`"
      :image="deferredImageUrl!"
      :background="background!"
      :color="color!"
      :icon="iconForAvatar!"
      :name="avatarName"
      :size="size!"
      :square="isRoom || square"
      :hide-border="hideBorder"
      :class="{
        'bg-surf-brand-base-def!': isOwn
      }"
    />
  </div>
</template>

<script lang="ts" setup>
import { IconName, WuiAvatar } from '@wui/common-library'
import { ContactState } from '@wui/im'
import { computed, onBeforeUnmount, onMounted, ref, toRefs } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { useConferenceIcon } from '@/shared/composables/use-conference-icon'
import { useUserColor } from '@/shared/composables/user-color'
import { Contact } from '@/shared/types/api'
import { RoomListItem } from '@/shared/types/app'
import { Room } from '@/shared/types/store'
import { getAuthenticatedMediaUrl } from '@/shared/utils/url'
import { getUserAvatarUrl } from '@/shared/utils/user-avatar'

/* eslint-disable vue/require-default-prop -- optional props omitted from defaults to satisfy exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  isRoom?: boolean | undefined
  isOwn?: boolean
  square?: boolean
  /** When false, show initials only (no appimage/network). Chat list passes visibility. */
  loadImage?: boolean
  hideBorder?: boolean
  name?: string | undefined
  icon?: IconName | undefined
  contactType?: ContactState['type'] | undefined
  size?: 24 | 32 | 40 | 48 | 56 | 64 | 80 | 96 | 120 | 160 | 200 | undefined
  room?: Room | RoomListItem | undefined
  contact?: Contact | undefined
}>(), {
  isRoom: false,
  size: 48,
  square: false,
  loadImage: true,
  hideBorder: false,
})
/* eslint-enable vue/require-default-prop */

const { contact, icon, name, size, isOwn, square, hideBorder } = toRefs(props)

const { getColorByName } = useUserColor()
const { t } = useLocalization()

const iconForAvatar = computed<IconName | undefined>(() => {
  if (icon.value) {
    return icon.value
  }
  if (props.contactType === 'pstnGuest') {
    return 'callUnknown'
  }
  if (props.isOwn) {
    return 'favoritesM'
  }
  if ((props.room?.name && props.room.name.startsWith(t('calls.fastConference')))) {
    return useConferenceIcon().getConferenceIcon(props.room.name)
  } else if ((props.name && props.name.startsWith(t('calls.fastConference')))) {
    return useConferenceIcon().getConferenceIcon(props.name)
  } else if (props.contact?.name && props.contact.name.startsWith(t('calls.fastConference'))) {
    return useConferenceIcon().getConferenceIcon(props.contact.name)
  }
  if (props.room?.name && props.room?.name.startsWith(t('mainAside.moderatedConference'))) {
    return 'shield'
  }
  return undefined
})

const background = computed(() => {
  if (props.contactType === 'pstnGuest') {
    return 'comp-avatar-user-purple'
  }
  const colorSource = props.room?.name || contact.value?.name || name.value
  if (!colorSource) {
    return 'primary'
  }

  return getColorByName(colorSource, props.isRoom)
})

/** Vibrant theme fills: white initials/icons for contrast (design avatar40). */
const color = computed(() => 'white')

const rootEl = ref<HTMLElement | null>(null)
const isInViewport = ref(typeof IntersectionObserver === 'undefined')
let intersectionObserver: IntersectionObserver | null = null

onMounted(() => {
  if (typeof IntersectionObserver === 'undefined' || !rootEl.value) {
    isInViewport.value = true
    return
  }
  intersectionObserver = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      isInViewport.value = true
      intersectionObserver?.disconnect()
      intersectionObserver = null
    }
  }, { rootMargin: '200px' })
  intersectionObserver.observe(rootEl.value)
})

onBeforeUnmount(() => {
  intersectionObserver?.disconnect()
  intersectionObserver = null
})

const imageUrl = computed(() => {
  if (props.isOwn || !props.loadImage) {
    return undefined
  }

  if (props.room?.isGroup && props.room.avatar) {
    return getAuthenticatedMediaUrl(props.room.avatar)
  }

  if (props.contact?.isPhotoAvailable && !props.room?.isGroup) {
    const contactId = props.contact.guid || props.contact.id
    return getUserAvatarUrl(contactId)
  }

  return undefined
})

/** Defer photo fetch until the avatar approaches the viewport (lists). */
const deferredImageUrl = computed(() => (isInViewport.value ? imageUrl.value : undefined))

const getAvatarShortName = (s_: string) => {
  const s = s_.trim()
  if (s.includes(',')) {
    return s
  }
  if (!s.includes(' ')) {
    return `${s.charAt(0)}${s.charAt(1)}`.toUpperCase()
  }
  const [lastName, firstName] = s.split(' ')
  return `${lastName} ${firstName}`
}

const avatarName = computed(() => {
  if (props.isRoom && props.room) {
    return getAvatarShortName(props.room.name)
  }
  if (props.name) { // OVERRIDE
    return getAvatarShortName(props.name || '')
  }

  if (props.contact) {
    if (props.contact?.lastName && props.contact?.firstName) {
      return `${props.contact.lastName} ${props.contact.firstName}`
    }

    return getAvatarShortName(props.contact.name || '')
  }

  return ''
})

</script>
